import { CapabilityRegistryContainer } from '@trimble/web3d';
import { SelectionSyncCoordinator } from '@trimble/pane-sync';
import { ObjectSelectionToolbarContainer as SharedObjectSelectionToolbarContainer } from '@trimble/ui-components/solidjs';
import { Component, createMemo, Show } from 'solid-js';
import {
  ClippingCapability,
  ObjectPropertiesPanelCapability,
  SelectionActionsCapability,
} from '@trimble/web3d-capabilities';

export interface ObjectSelectionToolbarContainerProps {
  capabilityRegistryContainer: CapabilityRegistryContainer;
  /** CSS classes to forward to the toolbar (e.g. padding overrides) */
  customClasses?: string;
}

/**
 * Lightweight wrapper around the shared ObjectSelectionToolbarContainer component.

 * The main purpose is to provide the shared component with the necessary capabilities and data
 * from the mobile-mapping specific context.
 */
export const ObjectSelectionToolbarContainer: Component<
  ObjectSelectionToolbarContainerProps
> = (props) => {
  const clippingCapability = createMemo(() =>
    props.capabilityRegistryContainer.get(ClippingCapability)()
  );

  const propertiesPanelCapability = createMemo(() =>
    props.capabilityRegistryContainer.get(ObjectPropertiesPanelCapability)()
  );

  const objectSelectionToolbarContainerProps = createMemo(() => {
    const viewerWrapper = props.capabilityRegistryContainer.viewerWrapper();
    if (viewerWrapper == null) return undefined;

    const syncCoordinator = props.capabilityRegistryContainer
      .coordinatorsWithParent()
      .find((c) => c instanceof SelectionSyncCoordinator);
    if (syncCoordinator == null) return undefined;

    const actionsCapability = props.capabilityRegistryContainer.get(
      SelectionActionsCapability
    )();
    if (actionsCapability == null) return undefined;

    return {
      viewerWrapper: viewerWrapper,
      selectionSyncCoordinator: syncCoordinator,
      selectionActionsCapability: actionsCapability,
    };
  });

  return (
    <Show when={objectSelectionToolbarContainerProps()} keyed>
      {(toolbarProps) => (
        <SharedObjectSelectionToolbarContainer
          viewerWrapper={toolbarProps.viewerWrapper}
          selectionSyncCoordinator={toolbarProps.selectionSyncCoordinator}
          selectionActionsCapability={toolbarProps.selectionActionsCapability}
          clippingCapability={clippingCapability()}
          propertiesPanelCapability={propertiesPanelCapability()}
          customClasses={props.customClasses}
          darkMode={true}
        />
      )}
    </Show>
  );
};
